import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

// Fix W (2026-10-13): manual en PDF (el alumno lo baja al pagar la reserva) y contrato propio del curso
const EMPTY = { name: "", description: "", module: "", group: "", base_price: "", practices_required: "0", reservation_amount: "", is_active: true, manual_url: "", manual_name: "", contract_text: "", diploma_template_url: "" };

// Fix U (2026-10-13): una certificacion de la academia.
export default function CourseModal({ open, onOpenChange, course, defaultReservation, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const isEdit = Boolean(course);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(course ? {
      name: course.name || "", description: course.description || "", base_price: String(course.base_price ?? ""),
      practices_required: String(course.practices_required ?? 0),
      reservation_amount: course.reservation_amount == null ? "" : String(course.reservation_amount), is_active: course.is_active !== false,
      manual_url: course.manual_url || "", manual_name: course.manual_name || "", contract_text: course.contract_text || "",
      diploma_template_url: course.diploma_template_url || "", // Fix Y (2026-10-13)
      module: course.module || "", group: course.group || "", // Fix J (2026-10-27)
    } : EMPTY);
  }, [open, course]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadManual = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, manual_url: data.url, manual_name: file.name }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  // Fix Y (2026-10-13): fondo del diploma (imagen tal cual, sin achicar, porque se imprime)
  const uploadDiploma = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?optimize=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, diploma_template_url: data.url }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (form.name.trim().length < 2 || form.base_price === "") {
      setError("Nombre y precio normal son obligatorios");
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      name: form.name.trim(), description: form.description.trim() || null, base_price: Number(form.base_price) || 0,
      practices_required: Number(form.practices_required) || 0, is_active: form.is_active,
      manual_url: form.manual_url, manual_name: form.manual_name, contract_text: form.contract_text.trim(),
      diploma_template_url: form.diploma_template_url,
      module: form.module.trim(), group: form.group.trim(),
    };
    if (form.reservation_amount !== "") body.reservation_amount = Number(form.reservation_amount);
    else if (isEdit) body.clear_reservation_amount = true;
    try {
      const { data } = isEdit ? await api.put(`/academy/courses/${course.id}`, body) : await api.post("/academy/courses", body);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.courseModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar curso" : "Nuevo curso"}</DialogTitle>
          <DialogDescription className="sr-only">Datos de la certificación</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre de la certificación</Label>
            <Input value={form.name} onChange={set("name")} data-testid={ACADEMY.courseNameInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Descripción</Label>
            <Textarea rows={3} value={form.description} onChange={set("description")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Módulo (web pública)</Label>
              <Input value={form.module} onChange={set("module")} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Grupo dentro del módulo</Label>
              <Input value={form.group} onChange={set("group")} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Precio normal</Label>
              <Input type="number" min="0" step="0.01" value={form.base_price} onChange={set("base_price")} data-testid={ACADEMY.coursePriceInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Reserva</Label>
              <Input type="number" min="0" step="0.01" value={form.reservation_amount} onChange={set("reservation_amount")} placeholder={`${defaultReservation ?? 200} (general)`} data-testid={ACADEMY.courseReservationInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Prácticas</Label>
              <Input type="number" min="0" value={form.practices_required} onChange={set("practices_required")} data-testid={ACADEMY.coursePracticesInput} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Manual del curso (PDF)</Label>
            <div className="flex flex-wrap items-center gap-2">
              {form.manual_url && (
                <a href={getFileUrl(form.manual_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#8A6A2F]" data-testid={ACADEMY.courseManualLink}><FileText className="w-4 h-4" /> {form.manual_name || "Manual"}</a>
              )}
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-white text-sm cursor-pointer">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {form.manual_url ? "Cambiar" : "Subir PDF"}
                <input type="file" accept="application/pdf" className="hidden" onChange={uploadManual} disabled={uploading} data-testid={ACADEMY.courseManualInput} />
              </label>
              {form.manual_url && <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, manual_url: "", manual_name: "" }))}><X className="w-4 h-4" /> Quitar</Button>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">El alumno lo descarga desde su página cuando paga la reserva.</p>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fondo del diploma (imagen horizontal, JPG o PNG)</Label>
            <div className="flex flex-wrap items-center gap-2">
              {form.diploma_template_url && <img src={getFileUrl(form.diploma_template_url)} alt="Fondo del diploma" className="h-14 rounded border border-border" data-testid={ACADEMY.courseDiplomaPreview} />}
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-white text-sm cursor-pointer">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {form.diploma_template_url ? "Cambiar" : "Subir imagen"}
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadDiploma} disabled={uploading} data-testid={ACADEMY.courseDiplomaInput} />
              </label>
              {form.diploma_template_url && <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, diploma_template_url: "" }))}><X className="w-4 h-4" /> Quitar</Button>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Sin imagen se usa un diseño simple. El nombre, el curso y la fecha se escriben encima, centrados.</p>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Contrato de este curso</Label>
            <Textarea rows={5} value={form.contract_text} onChange={set("contract_text")} placeholder="Vacío = se usa el contrato general de Configuración" data-testid={ACADEMY.courseContractInput} />
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              Curso activo
            </label>
          )}
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving || uploading} data-testid={ACADEMY.courseSaveButton}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
